<template>
  <div>
    <WdsAlert v-if="batchedInvoices" info role="alert" class="tw-my-2">
      <!-- eslint-disable max-len -->
      {{
        $t(
          'You currently use the batch invoicing feature, which means that this view also shows all the invoices that are related to multiple bills and PO numbers.',
        )
      }}
    </WdsAlert>
    <WdsAlert v-if="window.currentAccountType === 'organisation'" info role="alert" class="tw-my-2">
      <!-- eslint-disable max-len -->
      {{
        $t(
          'This page displays invoices from companies where you have membership. If invoices are missing, please verify your access with your team.',
        )
      }}
    </WdsAlert>
    <TableInvoiceFilters :currencies="billsCurrencies" @filters-updated="filterUpdated" />
    <div class="card">
      <BaseDataTable
        v-if="filterReady"
        multi-select
        :entries="tableData.rows"
        :columns="tableData.columns"
        :settings="settings"
        :loading="$apollo.loading"
        :paginator-info="paginator"
        class="f-12"
        @rows-selected="selectedRows = $event"
        @search="search"
      >
        <template #additional-control-bar-elements>
          <ButtonDropdown caret outline size="sm" class="m-l-5">
            {{ $t('Actions') }}
            <template #dropdown>
              <ul class="base-dropdown-menu" style="display: inline-block; position: static">
                <li :class="{ disabled: !allSelectedRowsHaveInvoices }">
                  <BaseHtmlForm action="/billing/invoices/zip" method="POST">
                    <input
                      v-for="row in selectedRows"
                      :key="row.id"
                      type="hidden"
                      name="ids[]"
                      :value="row.id"
                    />
                    <component
                      :is="allSelectedRowsHaveInvoices ? 'button' : 'a'"
                      v-tooltip="
                        !allSelectedRowsHaveInvoices &&
                        $t(
                          'Your selection includes invoices that are not ready to download yet. Please change your selection.',
                        )
                      "
                      class="no-spinner"
                      v-bind="allSelectedRowsHaveInvoices ? { type: 'submit' } : { href: '#' }"
                    >
                      <i class="fa fa-download m-r-5" aria-hidden="true"></i>
                      {{ $t('Download selected invoices') }}
                    </component>
                  </BaseHtmlForm>
                </li>
              </ul>
            </template>
          </ButtonDropdown>
        </template>
        <template #division="{ row }">
          {{ $t(row.company.name) }}
        </template>
        <template #localId="{ row }">
          <BaseLabel basic class="m-r-5 f-11" :text="`#${row.id}`" />
        </template>
        <template #type="{ row }">
          {{ $t(row.transactionType) }}
        </template>
        <template #number="{ row }">
          <span :class="{ 'tw-text-gray-600': row.isFullyCredited }">
            <span class="tw-font-bold">{{ row.number }}</span>
            <!-- prettier-ignore -->
            <template v-if="row.originalInvoice || row.creditNotes.length">,<br></template>
            <template v-if="row.originalInvoice?.number">
              {{ row.originalInvoice.number }}
            </template>
            <template v-else-if="row.creditNotes.length === 1">
              {{ row.creditNotes[0].number }}
            </template>
            <template v-else-if="row.creditNotes.length > 1">
              <ProvideStateTriggeredBySlotProps v-slot="{ toggle, isOn }">
                <span v-if="!isOn" class="tw-cursor-pointer" @click="toggle">
                  {{ row.creditNotes[0].number }} +{{ row.creditNotes.length - 1 }}
                </span>
                <span v-if="isOn">
                  <!-- eslint-disable-next-line vue/require-v-for-key -- Baseline eslint ignore please fix if working on this-->
                  <span v-for="(creditNote, key) in row.creditNotes">
                    <!-- prettier-ignore -->
                    <template v-if="key !== row.creditNotes.length - 1">{{ creditNote.number }},<br /></template>
                    <template v-else>{{ creditNote.number }}</template>
                  </span>
                </span>
              </ProvideStateTriggeredBySlotProps>
            </template>
          </span>
        </template>
        <template #billIds="{ row }">
          <CollapsibleLabels
            v-if="row.billIds !== undefined"
            v-slot="{ loopValue }"
            basic
            label-class="di-block m-r-5 f-11"
            :values="row.billIds"
          >
            <PopoverBillPublicApi
              v-if="window.currentAccountType !== 'organisation'"
              :global-id="loopValue.id"
              :bill-number="loopValue.number"
            />
            <BaseLabel v-else basic class="m-r-5 f-11" :text="`#${loopValue.number}`" />
          </CollapsibleLabels>
        </template>
        <template #poNumbers="{ row }">
          <span v-if="!row.poNumbers?.length">-</span>
          <CollapsibleLabels
            v-else
            v-slot="{ propsValue, labelClassValue, loopValue }"
            basic
            label-class="di-block m-r-5 f-11"
            :values="row.poNumbers"
          >
            <span class="tw-inline-block tw-max-w-[200px] tw-truncate" :title="loopValue">
              <BaseLabel v-bind="propsValue" :class="labelClassValue" :text="loopValue" />
            </span>
          </CollapsibleLabels>
        </template>
        <template #freelancer="{ row }">
          <TdFreelancer
            v-if="!row.isBatched && row.rows !== null"
            :global-id="row.rows.data[0]?.worker?.id"
            :name="row.rows.data[0]?.worker?.name"
            :profile-url="getProfileView(row.rows.data[0]?.worker?.profile?.id)"
          />
        </template>
        <template #pdfUrl="{ row }">
          <a
            v-if="row.pdfUrl"
            :href="row.pdfUrl"
            :title="$t('Download invoice as PDF')"
            target="_blank"
            class="c-darkgreen"
          >
            <WdsIcon name="document-download" :size="18" />
          </a>
          <span
            v-else
            :title="
              $t('PDF is not available for invoice :invoiceno.', {
                invoiceno: row.id || '',
              }) + ' '
            "
            data-toggle="tooltip"
            class="c-gray"
          >
            <WdsIcon name="document-download" :size="18" />
          </span>
        </template>
        <template #status="{ row }">
          <BaseLabel v-if="row.isFullyCredited" :text="$t('Credited')" danger></BaseLabel>
          <BaseLabel
            v-else-if="row.transactionType === 'CREDIT_NOTE'"
            :text="$t('Allocated')"
            default
          ></BaseLabel>
          <BaseLabel v-else-if="row.markedPaidAt !== null" :text="$t('Paid')" success></BaseLabel>
          <BaseLabel
            v-else-if="daysOverdue(row) > 0"
            v-tooltip="`${daysOverdue(row)} ${$t('days over due')}`"
            :text="$t('Overdue')"
            warning
          ></BaseLabel>
          <BaseLabel v-else :text="$t('Unpaid')" default></BaseLabel>
        </template>
        <template #due="{ row }">
          <TdDate v-if="row.transactionType !== 'CREDIT_NOTE'" :date="row.dueDate" />
          <span v-else>-</span>
        </template>
      </BaseDataTable>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import gql from 'graphql-tag'
import { markRaw } from 'vue'
import { WdsAlert, WdsIcon } from '@worksome/wds-components'
import TableInvoiceFilters from '@/components/TableInvoiceFilters.vue'
import HashMixin from '@/mixins/MethodHash'
import { TableDataHandler } from '@/vue-prop-types/DataTable'
import { cleanStrict, stringToEnum } from '@/utils/tools'
import BaseDataTable from '@/components/BaseDataTable'
import { TdDate, TdFreelancer } from '@/components/BaseDataTable/Cells'
import ButtonDropdown from '@/components/ButtonDropdown.vue'
import BaseHtmlForm from '@/components/BaseHtmlForm.vue'
import BaseLabel from '@/components/BaseLabel.vue'
import CollapsibleLabels from '@/components/CollapsibleLabels.vue'
import { LocalStorageEncoder, queryDecodeObject } from '@/utils/Encoders'
import PopoverBill from '@/components/PopoverBill.vue'
import { toFormattedAmountByLocale } from '@/utils/currency'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import PopoverBillPublicApi from '@/components/PopoverBillPublicApi.vue'
import ProvideStateTriggeredBySlotProps from '@/components/ProvideStateTriggeredBySlotProps.vue'

export default {
  name: 'TableInvoice',
  components: {
    WdsAlert,
    ProvideStateTriggeredBySlotProps,
    WdsIcon,
    PopoverBillPublicApi,
    TdFreelancer,
    CollapsibleLabels,
    BaseLabel,
    TableInvoiceFilters,
    TdDate,
    ButtonDropdown,
    BaseHtmlForm,
    BaseDataTable,
    PopoverBill,
  },
  mixins: [HashMixin],
  props: {
    batchedInvoices: {
      type: Boolean,
      default: false,
    },
    billsCurrencies: {
      type: Array,
      default: () => [],
    },
    accountIds: {
      type: Array,
      default: null,
    },
  },
  apollo: {
    $client: 'publicApi',
    invoices: {
      skip() {
        return !this.filterReady
      },
      query: gql`
        query Invoices(
          $accounts: [ID!]
          $status: [InvoiceStatus!]
          $currency: [Currency!]
          $transactionTypes: [InvoiceTransactionType!]
          $search: String
          $hasPurchaseOrderNumber: Boolean
          $page: Int!
          $count: Int!
          $orderBy: [QueryInvoicesOrderByOrderByClause!]
        ) {
          invoices(
            accounts: $accounts
            status: $status
            currency: $currency
            transactionTypes: $transactionTypes
            search: $search
            hasPurchaseOrderNumber: $hasPurchaseOrderNumber
            first: $count
            page: $page
            orderBy: $orderBy
          ) {
            paginatorInfo {
              ...PaginatorInfoFragment
              count
              currentPage
              total
            }
            data {
              id
              number
              currency
              grossAmount
              grossOpenAmount
              pdfUrl
              isBatched
              markedPaidAt
              date
              dueDate
              transactionType
              isFullyCredited
              creditNotes {
                number
              }
              originalInvoice {
                number
              }
              rows(first: 100) {
                data {
                  worker {
                    id
                    name
                    profile {
                      id
                    }
                  }
                  paymentRequest {
                    id
                    number
                    purchaseOrderNumber
                  }
                }
              }
              company {
                id
                name
              }
              payerDisplayName
              payeeDisplayName
            }
          }
        }
        ${BaseDataTable.fragments.pagination}
      `,
      debounce: 250,
      variables() {
        return {
          accounts: this.accountIds || [this.account.globalId],
          status: this.filter?.invoiceStatus || [],
          currency: this.filter?.currencies || [],
          transactionTypes: this.filter?.invoiceType || [],
          search: this.searchValue,
          hasPurchaseOrderNumber: this.filter?.hasPoNumber,
          page: this.paginationSettings.page,
          count: this.paginationSettings.displayPerPage,
          orderBy: this.ordering,
        }
      },
    },
  },
  data() {
    const odSettings = new LocalStorageEncoder({
      defaultVal: { date: 'desc' },
      storageKey: 'ordering-v2',
      decoder: queryDecodeObject,
      filter(data) {
        return cleanStrict({ ...data })
      },
    })
    return {
      filter: {},
      action: 'default',
      paginationSettings: {
        page: 1,
        displayPerPage: 50,
      },
      orderSettings: odSettings,
      searchValue: '',
      header: [
        {
          key: 'company',
          title: $t('Division'),
          slot: 'division',
          class: 'nowrap',
          orderAble: false,
          hide: window.currentAccountType !== 'organisation',
        },
        {
          key: 'date',
          title: $t('Issued date'),
          slot: 'Date',
          class: 'nowrap',
          renderUsing: {
            component: markRaw(TdDate),
            componentProps: (row) => ({ date: row.date }),
          },
        },
        {
          key: 'type',
          title: $t('Type'),
          slot: 'type',
          orderAble: false,
          class: 'text-left',
        },
        {
          key: 'number',
          slot: 'number',
          title: $t('Invoice'),
          sort: 'id',
          class: 'text-left',
          ignoreClickForRowSelection: true,
        },
        {
          key: 'billIds',
          slot: 'billIds',
          title: $t('Bills'),
          orderAble: false,
          class: 'text-left',
          ignoreClickForRowSelection: true,
        },
        {
          key: 'poNumbers',
          slot: 'poNumbers',
          title: $t('PO no.'),
          class: 'text-left',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          title: $t('Freelancer'),
          key: 'freelancer',
          slot: 'freelancer',
          class: 'text-left',
          orderAble: false,
          ignoreClickForRowSelection: true,
        },
        {
          key: 'payer',
          title: $t('Payer'),
          class: 'text-left',
          orderAble: false,
        },
        {
          key: 'payee',
          title: $t('Payee'),
          class: 'text-left',
          orderAble: false,
        },
        {
          key: 'pdfUrl',
          slot: 'pdfUrl',
          title: $t('Document'),
          class: 'text-left f-13 nowrap',
          orderAble: false,
          ignoreClickForRowSelection: true,
          hide: window.currentAccountType === 'organisation',
        },
        {
          key: 'totalAmount',
          title: $t('Invoice Total'),
          class: 'f-600 f-13',
        },
        {
          key: 'openAmount',
          title: $t('Open'),
          class: 'f-600 f-13',
        },
        {
          key: 'status',
          slot: 'status',
          title: $t('Status'),
          orderAble: false,
        },
        {
          key: 'dueDate',
          title: $t('Due date'),
          slot: 'due',
          class: 'nowrap',
        },
      ].map((h) => {
        // eslint-disable-next-line no-param-reassign -- Baseline eslint ignore please fix if working on this
        h.order = odSettings.data[h.key]
        return h
      }),
      loading: true,
      currentUrl: this.getCurrentUrl(),
      selectedRows: {},
    }
  },
  computed: {
    filterReady() {
      return Object.keys(this.filter).length > 0
    },
    moment: () => moment,
    settings() {
      return {
        pageLength: this.paginationSettings.displayPerPage, // Elements per page
        order: this.ordering, // Column ordering
        search: { search: this.searchValue }, // Current search
        displayStart: (this.paginationSettings.page - 1) * this.paginationSettings.displayPerPage, // Page number
        autoWidth: false, // Stops resizing issues from occurring
      }
    },
    invoicesList() {
      return this.invoices?.data || []
    },
    rows() {
      return this.invoicesList.map((instance) => ({
        ...instance,
        billIds: instance.rows.data
          .filter((entryObj) => entryObj.paymentRequest !== null)
          .map((entryObj) => ({
            id: entryObj.paymentRequest.id,
            number: entryObj.paymentRequest.number,
          })),
        poNumbers: instance.rows.data
          .map((entryObj) => entryObj.paymentRequest?.purchaseOrderNumber)
          .filter(Boolean)
          .map((poNumber) => String(poNumber)),
        totalAmount: `${instance.currency} ${
          instance.transactionType === 'CREDIT_NOTE' ? '-' : ''
        }${toFormattedAmountByLocale(instance.grossAmount.toFixed(2))}`,
        openAmount: `${instance.currency} ${toFormattedAmountByLocale(
          instance.grossOpenAmount.toFixed(2),
        )}`,
        payer: instance.payerDisplayName || '-',
        payee: instance.payeeDisplayName || '-',
        pdfUrl: instance.pdfUrl,
      }))
    },
    tableData() {
      return new TableDataHandler({
        rows: this.rows,
        columns: this.header,
      })
    },
    paginator() {
      return this?.invoices?.paginatorInfo
    },
    totalElements() {
      return this?.data?.paginatorInfo?.total || 0
    },
    ordering() {
      const o = []
      for (const key in this.orderSettings.data) {
        if (this.orderSettings.data[key]) {
          o.push({
            column: stringToEnum(key),
            order: stringToEnum(this.orderSettings.data[key]),
          })
        }
      }
      return o
    },
    allSelectedRowsHaveInvoices() {
      const rows = Object.values(this.selectedRows)

      return rows.length > 0 && rows.every((row) => row.pdfUrl)
    },
  },
  methods: {
    search(options) {
      this.paginationSettings.displayPerPage = options.perPage
      this.paginationSettings.page = options.page + 1
      this.orderSettings.data = options.order
      this.searchValue = options.search
    },
    filterUpdated($event) {
      this.currentUrl = this.getCurrentUrl()
      // Since the filter might fire at random, we only update our copy when the new filter is different from ours.
      // We also reset out page to one, since the amount of data we have might change such that there are fewer pages
      // for us to paginate.
      if (this.hash(this.filter) !== this.hash($event)) {
        this.filter = $event
        this.paginationSettings.page = 1
      }
    },
    getCurrentUrl() {
      return encodeURIComponent(window.location.pathname + window.location.search)
    },
    daysOverdue(invoice) {
      if (!invoice.dueDate) {
        return 0
      }

      return moment().diff(moment(invoice.dueDate), 'days')
    },
    getProfileView(id) {
      if (id === null) {
        return ''
      }

      return laravelNamedRoute('frontend.user.profile.view', { profile: id })
    },
  },
}
</script>
